import { z } from 'zod/v4';

/**
 * Checks the rows are an array of arrays and hands back the very same value. `z.array(z.array(z.any()))`
 * would rebuild both levels to say the same thing, which on a large grid means a second copy of the whole
 * payload — 1.3M fresh row arrays for the associated filter values response — held alongside the original
 * while the rows below are built.
 */
const gridRowsSchema = z.custom<Array<Array<unknown>>>(
  (value) => Array.isArray(value) && value.every((row) => Array.isArray(row)),
);

const gridResponseSchema = z
  .object({
    columns: z.array(z.string()),
    data: gridRowsSchema.optional(),
  })
  .loose(); // essential to allow extra fields through - e.g. `count`

export const gridData = <OutputSchema extends z.ZodObject<{ data: z.ZodArray<z.ZodType> }>>(
  outputSchema: OutputSchema,
) =>
  z.preprocess(
    (response) => {
      const { success, data: parsedResponse } = gridResponseSchema.safeParse(response);

      if (!success) {
        return undefined;
      }

      const { data, columns, ...rest } = parsedResponse;

      if (data) {
        type RowItem = Record<string, unknown>;

        // oxlint-disable-next-line unicorn/no-new-array -- intentional preallocation of a fixed-length array
        const rowData = new Array<RowItem>(data.length);

        // Rows built key by key on a bare object end up in V8's slow dictionary mode on real grids, and the
        // zod output copies that shape. Spreading one template that already holds every column gives all rows
        // of the response a single fast shape: 1.3M associated filter values rows drop from 3.2 GB to 1.5 GB
        // once parsed.
        const template = Object.fromEntries(columns.map((column) => [column, undefined])) as RowItem;

        for (let i = 0; i < data.length; i++) {
          const row: RowItem = { ...template };

          for (let j = 0; j < columns.length; j++) {
            row[columns[j]] = data[i][j];
          }

          rowData[i] = row;
        }

        return { ...rest, columns, data: rowData };
      }

      return rest;
    },

    outputSchema,
  );
